import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useSubscription } from './useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SmartAvailabilitySyncConfig {
  enabled: boolean;
  max_connections_free: number;
  max_connections_pro: number;
}

interface CalendarConnection {
  id: string;
  guest_id: string;
  event_id: string;
  calendar_provider: string;
  calendar_email: string;
  sync_status: string;
  last_sync_at: string | null;
}

interface UseSmartAvailabilitySyncProps {
  eventId?: string;
  guestId?: string;
}

export const useSmartAvailabilitySync = ({ eventId, guestId }: UseSmartAvailabilitySyncProps = {}) => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { toast } = useToast();
  
  const [config, setConfig] = useState<SmartAvailabilitySyncConfig>({
    enabled: false,
    max_connections_free: 3,
    max_connections_pro: 999,
  });
  const [connections, setConnections] = useState<CalendarConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);

  // Load system configuration
  const loadConfig = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'smart_availability_sync')
        .single();

      if (error) {
        console.error('Error loading Smart Availability Sync config:', error);
        return;
      }

      if (data?.setting_value) {
        const configValue = data.setting_value as any;
        const newConfig = {
          enabled: configValue.enabled ?? false,
          max_connections_free: configValue.max_connections_free ?? 3,
          max_connections_pro: configValue.max_connections_pro ?? 999,
        };
        setConfig(newConfig);
        setIsFeatureEnabled(newConfig.enabled);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  }, []);

  // Load calendar connections for the current guest/event
  const loadConnections = useCallback(async () => {
    if (!eventId || !guestId || !isFeatureEnabled) {
      setConnections([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('guest_calendar_connections')
        .select('*')
        .eq('event_id', eventId)
        .eq('guest_id', guestId);

      if (error) {
        console.error('Error loading calendar connections:', error);
        return;
      }

      setConnections(data || []);
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  }, [eventId, guestId, isFeatureEnabled]);

  // Initialize data
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadConfig();
      setLoading(false);
    };
    init();
  }, [loadConfig]);

  // Load connections when config is ready
  useEffect(() => {
    if (isFeatureEnabled) {
      loadConnections();
    }
  }, [isFeatureEnabled, loadConnections]);

  // Calculate user limits
  const getUserLimits = useCallback(() => {
    if (!isFeatureEnabled) {
      return { maxConnections: 0, currentConnections: 0, canConnect: false };
    }

    const maxConnections = subscription.subscribed 
      ? config.max_connections_pro 
      : config.max_connections_free;
    
    const currentConnections = connections.length;
    const canConnect = currentConnections < maxConnections;

    return {
      maxConnections,
      currentConnections,
      canConnect,
      isProUser: subscription.subscribed,
    };
  }, [isFeatureEnabled, subscription.subscribed, config, connections]);

  // Check if calendar sync is available for current event
  const isCalendarSyncAvailable = useCallback(async (checkEventId: string) => {
    if (!isFeatureEnabled) return false;

    try {
      const { data, error } = await supabase
        .from('events')
        .select('calendar_sync_enabled')
        .eq('id', checkEventId)
        .single();

      if (error) {
        console.error('Error checking event calendar sync status:', error);
        return false;
      }

      return data?.calendar_sync_enabled === true;
    } catch (error) {
      console.error('Error checking calendar sync availability:', error);
      return false;
    }
  }, [isFeatureEnabled]);

  // Get availability status for an option
  const getAvailabilityStatus = useCallback(async (
    optionType: 'date' | 'time' | 'location',
    optionValue: string
  ) => {
    if (!eventId || !guestId || !isFeatureEnabled || connections.length === 0) {
      return 'not_connected';
    }

    try {
      // Parse the option value to create start and end times
      let startTime: string;
      let endTime: string;

      if (optionType === 'date') {
        try {
          // For date options, check the whole day
          const date = new Date(optionValue);
          if (isNaN(date.getTime())) {
            console.warn('Invalid date value:', optionValue);
            return 'unknown';
          }
          startTime = new Date(date.setHours(0, 0, 0, 0)).toISOString();
          endTime = new Date(date.setHours(23, 59, 59, 999)).toISOString();
        } catch (dateError) {
          console.error('Error parsing date:', dateError);
          return 'unknown';
        }
      } else if (optionType === 'time') {
        try {
          // For time options, assume a 2-hour duration
          const [timeStr, dateStr] = optionValue.split(' on ');
          const baseDate = dateStr ? new Date(dateStr) : new Date();
          if (isNaN(baseDate.getTime())) {
            console.warn('Invalid base date:', dateStr);
            return 'unknown';
          }
          
          const timeParts = timeStr.split(':');
          if (timeParts.length !== 2) {
            console.warn('Invalid time format:', timeStr);
            return 'unknown';
          }
          
          const [hours, minutes] = timeParts.map(Number);
          if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            console.warn('Invalid time values:', hours, minutes);
            return 'unknown';
          }
          
          startTime = new Date(baseDate.setHours(hours, minutes, 0, 0)).toISOString();
          endTime = new Date(baseDate.setHours(hours + 2, minutes, 0, 0)).toISOString();
        } catch (timeError) {
          console.error('Error parsing time:', timeError);
          return 'unknown';
        }
      } else {
        // For location, we can't check availability
        return 'unknown';
      }

      const { data, error } = await supabase.functions.invoke('check-availability', {
        body: {
          event_id: eventId,
          guest_id: guestId,
          start_time: startTime,
          end_time: endTime,
        }
      });

      if (error) {
        console.error('Error checking availability:', error);
        return 'unknown';
      }

      return data?.availability_status || 'unknown';
    } catch (error) {
      console.error('Error in getAvailabilityStatus:', error);
      return 'unknown';
    }
  }, [eventId, guestId, isFeatureEnabled, connections]);

  // Connect to a calendar provider
  const connectCalendar = useCallback(async (provider: 'google' | 'microsoft' | 'apple') => {
    if (!eventId || !guestId) {
      throw new Error('Event ID and Guest ID are required');
    }

    const limits = getUserLimits();
    if (!limits.canConnect) {
      throw new Error(`Connection limit reached. ${limits.isProUser ? 'Maximum' : 'Upgrade to Pro for more'} connections allowed.`);
    }

    // This method is now handled by CalendarSyncPrompt component
    // The actual OAuth flow happens in the component's handleConnect method
    console.log(`Initiating ${provider} calendar connection for event ${eventId}, guest ${guestId}`);
    
    // Refresh connections after successful OAuth
    await loadConnections();
    
    toast({
      title: "Calendar connected",
      description: `Successfully connected your ${provider} calendar.`,
    });
  }, [eventId, guestId, getUserLimits, loadConnections, toast]);

  // Disconnect a calendar
  const disconnectCalendar = useCallback(async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from('guest_calendar_connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;

      await loadConnections();
      
      toast({
        title: "Calendar disconnected",
        description: "Your calendar has been disconnected from this event.",
      });
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect calendar. Please try again.",
        variant: "destructive",
      });
    }
  }, [loadConnections, toast]);

  return {
    // Configuration
    isFeatureEnabled,
    config,
    loading,
    
    // Connections
    connections,
    ...getUserLimits(),
    
    // Methods
    connectCalendar,
    disconnectCalendar,
    isCalendarSyncAvailable,
    getAvailabilityStatus,
    refreshConnections: loadConnections,
    refreshConfig: loadConfig,
  };
};